/*
  Сопоставление колонок выгрузки - §3.3.2.

  Экран подтверждения, о котором говорит конституция: система размечает файл
  словарём, человек проверяет и правит. До 08.09.2026 правки не было вовсе -
  сервер принимал сопоставление, а задать его было негде, и выгрузка с чужими
  заголовками не грузилась совсем.

  Рядом с каждой колонкой стоят её настоящие значения из файла. Без них
  сопоставление идёт вслепую: по заголовку «Объект» не понять, адрес там или
  номер дома, а ошибка уезжает цифрой в заявление.
*/

import { columns as columnsCount, rows as rowsCount } from '../api/format.js'
import Dropdown from './Select.jsx'

export default function ColumnMapping({ preview, choice, onChange, disabled }) {
  // Подпись строки списка - название колонки вместе с её значениями. Значения
  // нужны именно в момент выбора: по заголовку «Объект» не понять, адрес там
  // или номер дома, а к тому времени, когда колонка уже назначена, выбор сделан.
  const columns = Object.fromEntries(
    preview.headers.map((header) => [header, withSamples(header, preview.samples[header])]),
  )
  const taken = new Set(Object.values(choice).filter(Boolean))
  const missing = preview.fields.filter((item) => item.required && !choice[item.field])

  return (
    <section className="section card">
      <h2 className="section__title">Как Арбитр ЖКХ понял файл</h2>
      <div className="muted">
        В файле {rowsCount(preview.rows_total)} и {columnsCount(preview.headers.length)}. Проверьте
        соответствие: Арбитр ЖКХ угадывает по названию колонки, файл видите только вы.
      </div>

      {/*
        Выгрузка-приложение к договору начинается с названия документа и даты, и
        шапка стоит ниже. Пропустить эти строки молча нельзя: человек должен
        видеть, что Арбитр ЖКХ счёл шапкой, - иначе строка данных, принятая за
        заголовки, исчезает из портфеля незаметно.

        Число строк стоит в скобках, а не подлежащим: причастие при нём пришлось
        бы склонять вместе с существительным, и при одной пропущенной строке
        экран говорил бы «1 строка выше приняты и пропущены» (ревью 09.09.2026).
      */}
      {preview.header_row > 1 ? (
        <div className="muted">
          Шапка таблицы найдена в строке {preview.header_row}. Всё, что выше (
          {rowsCount(preview.header_row - 1)}), Арбитр ЖКХ считает заголовком документа и пропускает.
        </div>
      ) : null}

      {missing.length > 0 ? (
        <div className="notice notice--warn">
          <div className="notice__what">
            Не указаны обязательные поля: {missing.map((item) => item.title).join(', ')}.
          </div>
          <div className="notice__do">
            Выберите для каждого колонку из файла - без них начисление записать не из чего.
          </div>
        </div>
      ) : null}

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Поле</th>
              <th>Колонка в файле</th>
              <th>Значения из файла</th>
            </tr>
          </thead>
          <tbody>
            {preview.fields.map((item) => (
              <tr key={item.field}>
                <td id={`mapping-${item.field}`}>
                  {item.title}
                  {item.required ? <span className="muted"> обязательно</span> : null}
                </td>
                <td data-label="Колонка в файле">
                  <Dropdown
                    value={choice[item.field] ?? ''}
                    onChange={(header) => onChange(item.field, header)}
                    options={columns}
                    empty="нет такой колонки"
                    disabled={disabled}
                    labelledBy={`mapping-${item.field}`}
                  />
                </td>
                <td className="muted" data-label="Значения из файла">
                  {choice[item.field]
                    ? (preview.samples[choice[item.field]] ?? []).join(' · ') || 'колонка пустая'
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UnusedColumns headers={preview.headers} taken={taken} />
    </section>
  )
}

function withSamples(header, samples) {
  if (!samples || samples.length === 0) return `${header} - колонка пустая`
  return `${header} - ${samples.slice(0, 2).join(', ')}`
}

function UnusedColumns({ headers, taken }) {
  const unused = headers.filter((header) => !taken.has(header))
  if (unused.length === 0) return null
  return (
    <div className="muted">
      Колонки файла, которые никуда не назначены: {unused.join(', ')}. Это нормально, если в
      них лежит то, что для расчёта не нужно, - они просто не будут прочитаны.
    </div>
  )
}
